import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Loader2, ShieldAlert, TrendingUp, Zap, Radar, ArrowRight, Bot, RefreshCw, Clock,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { SecurityGauge } from "@/components/SecurityGauge";
import { SEVERITY } from "@/lib/security";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export default function Overview() {
  const [data, setData] = useState(null);
  const [scanning, setScanning] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const load = async () => {
    const { data } = await api.get("/dashboard/overview");
    setData(data);
  };
  useEffect(() => { load(); }, []);

  const runScan = async () => {
    setScanning(true);
    await new Promise((r) => setTimeout(r, 2200));
    await load();
    setScanning(false);
    toast.success("Scan complete — posture updated");
  };

  if (!data) {
    return <div className="flex items-center justify-center py-32"><Loader2 className="animate-spin text-cyber" size={30} /></div>;
  }

  const sc = data.severity_counts;
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-ink">
            Security Posture
          </h1>
          <p className="text-sm text-muted mt-1">
            Welcome back, {user?.name?.split(" ")[0] || "Agent"} — here's your organization's live risk overview.
          </p>
        </div>
        <Button onClick={runScan} disabled={scanning} data-testid="run-scan-btn"
          className="bg-cyber text-[#0B0E14] hover:bg-cyber-dim font-semibold">
          {scanning ? <Loader2 size={16} className="mr-2 animate-spin" /> : <RefreshCw size={16} className="mr-2" />}
          {scanning ? "Scanning…" : "Run Scan"}
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Gauge */}
        <div className="card-surface rounded-2xl p-6 flex flex-col items-center justify-center">
          <SecurityGauge score={data.score} size={190} />
          <div className="mt-4 flex items-center gap-1.5 text-xs text-muted font-mono">
            <Clock size={12} /> Last scan just now
          </div>
        </div>

        {/* Severity breakdown */}
        <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4">
          {Object.entries(sc).map(([k, v], i) => {
            const meta = SEVERITY[k];
            const Icon = meta.icon;
            return (
              <motion.div key={k}
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                className="card-surface card-hover rounded-2xl p-5 cursor-pointer"
                onClick={() => navigate("/dashboard/findings")}
                data-testid={`severity-card-${k}`}>
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: meta.bg }}>
                    <Icon size={19} style={{ color: meta.color }} />
                  </div>
                  <span className="font-display font-extrabold text-3xl text-ink">{v}</span>
                </div>
                <p className="mt-3 text-sm font-semibold" style={{ color: meta.color }}>{meta.label}</p>
                <p className="text-xs text-muted">open findings</p>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Stat strip */}
      <div className="grid sm:grid-cols-3 gap-4">
        {[
          { icon: Radar, label: "Findings detected", value: data.total_findings, color: "#00F0FF" },
          { icon: Zap, label: "Priority risks", value: data.priority_risks, color: "#F59E0B" },
          { icon: ShieldAlert, label: "Need immediate action", value: data.immediate_action, color: "#FF3366" },
        ].map((s) => (
          <div key={s.label} className="card-surface rounded-2xl p-5 flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: `${s.color}18` }}>
              <s.icon size={20} style={{ color: s.color }} />
            </div>
            <div>
              <div className="font-display font-extrabold text-2xl text-ink">{s.value}</div>
              <div className="text-xs text-muted">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Scanners + CTA */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card-surface rounded-2xl p-6">
          <h3 className="font-display font-semibold text-ink mb-4">Active Scanners</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {data.scanners.map((s) => (
              <div key={s.key} className="flex items-center gap-3 rounded-xl border border-line p-3">
                <span className="w-2 h-2 rounded-full bg-secure live-dot shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-ink">{s.name}</p>
                  <p className="text-xs text-muted truncate">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="card-surface rounded-2xl p-6 flex flex-col justify-between glow-cyan">
          <div>
            <div className="w-11 h-11 rounded-xl bg-cyber/15 flex items-center justify-center mb-4">
              <Bot size={22} className="text-cyber" />
            </div>
            <h3 className="font-display font-semibold text-ink">Ask your AI Security Engineer</h3>
            <p className="text-sm text-muted mt-2">Get a prioritized fix plan, generate patches or draft compliance policies.</p>
          </div>
          <Button onClick={() => navigate("/dashboard/copilot")} data-testid="overview-copilot-btn"
            className="mt-5 bg-cyber text-[#0B0E14] hover:bg-cyber-dim font-semibold w-full">
            Open Copilot <ArrowRight size={16} className="ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}
